/**
 * UTM 파라미터 쿠키 관리 라이브러리
 *
 * 흐름:
 * 1. middleware에서 호출되어 쿠키에 url의 UTM 파라미터를 저장합니다.
 * 2. app download url을 생성(getDownloadLink)할 때, 쿠키에 저장된 파라미터를 추가합니다.
 */

interface UTMData {
  [key: string]: string
  timestamp: string
}

interface CompressedUTMData {
  s?: string // utm_source
  m?: string // utm_medium
  c?: string // utm_campaign
  t?: string // utm_term
  ct?: string // utm_content
  ts: string // timestamp
}

interface UTMParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  term?: string
}

export const UTM_COOKIE_NAME = 'utm_data'
export const UTM_EXPIRY_DAYS = 7 // 30일 → 7일로 단축

export function parseUTMFromCookie(cookieValue: string | undefined): UTMParams | null {
  try {
    if (!cookieValue) return null

    // 압축된 형태인지 확인 (Base64로 시작하는지)
    if (cookieValue.startsWith('eyJ')) {
      // Base64 JSON 시작 패턴
      return parseCompressedUTMFromCookie(cookieValue)
    }

    // 기존 형태 (하위 호환성)
    const decoded = decodeURIComponent(cookieValue)
    const data: UTMData = JSON.parse(decoded)

    // 유효기간 체크
    if (!data.timestamp) return null
    const timestamp = new Date(data.timestamp)
    const now = new Date()
    const diffDays = (now.getTime() - timestamp.getTime()) / (1000 * 60 * 60 * 24)

    if (diffDays > UTM_EXPIRY_DAYS) {
      return null
    }

    // timestamp 제거하고 UTM 파라미터만 반환
    const { timestamp: _, ...utmParams } = data
    return utmParams as UTMParams
  } catch (error) {
    console.error('utm-helper: parseUTMFromCookie() error', error, cookieValue)
    return null
  }
}

/**
 * 압축된 UTM 쿠키 파싱
 */
function parseCompressedUTMFromCookie(cookieValue: string): UTMParams | null {
  try {
    // Base64 디코딩
    const compressedJson = atob(cookieValue)
    const compressedData: CompressedUTMData = JSON.parse(compressedJson)

    // 유효기간 체크
    if (!compressedData.ts) return null
    const timestamp = new Date(compressedData.ts)
    const now = new Date()
    const diffDays = (now.getTime() - timestamp.getTime()) / (1000 * 60 * 60 * 24)

    if (diffDays > UTM_EXPIRY_DAYS) {
      return null
    }

    // 압축 해제
    const utmParams: UTMParams = {}
    if (compressedData.s) utmParams.utm_source = compressedData.s
    if (compressedData.m) utmParams.utm_medium = compressedData.m
    if (compressedData.c) utmParams.utm_campaign = compressedData.c
    if (compressedData.t) utmParams.utm_term = compressedData.t
    if (compressedData.ct) utmParams.utm_content = compressedData.ct

    return utmParams
  } catch (error) {
    console.error('utm-helper: parseCompressedUTMFromCookie() error', error, cookieValue)
    return null
  }
}

/**
 * 클라이언트 쿠키로부터 UTM 데이터를 가져옵니다.
 */
export function getUTMDataFromBrowser(): UTMParams | null {
  if (typeof window === 'undefined') return null
  try {
    const cookies = document.cookie.split(';').reduce(
      (acc, cookie) => {
        const [name, value] = cookie.trim().split('=')
        acc[name] = value
        return acc
      },
      {} as Record<string, string>,
    )

    return parseUTMFromCookie(cookies[UTM_COOKIE_NAME])
  } catch (error) {
    console.error('utm-helper: getUTMDataFromBrowser() error', error)
    return null
  }
}

/**
 * 서버에서 url로부터 UTM 파라미터를 추출하고 압축된 쿠키에 저장합니다.
 */
export const createUtmCookieValueFromUrl = (url: string): string | null => {
  try {
    const ALLOWED_UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

    // URL에서 UTM 파라미터 추출
    const utmParams: Record<string, string> = {}
    let hasUtmParams = false
    const urlObject = new URL(url)
    ALLOWED_UTM_PARAMS.forEach((param) => {
      const value = urlObject.searchParams.get(param)
      if (value) {
        utmParams[param] = value
        hasUtmParams = true
      }
    })

    // UTM 파라미터가 없으면 null 반환
    if (!hasUtmParams) return null

    // 압축된 데이터 생성
    const compressedData: CompressedUTMData = {
      ts: new Date().toISOString(),
    }

    if (utmParams.utm_source) compressedData.s = utmParams.utm_source
    if (utmParams.utm_medium) compressedData.m = utmParams.utm_medium
    if (utmParams.utm_campaign) compressedData.c = utmParams.utm_campaign
    if (utmParams.utm_term) compressedData.t = utmParams.utm_term
    if (utmParams.utm_content) compressedData.ct = utmParams.utm_content

    // Base64 인코딩으로 압축
    const compressedJson = JSON.stringify(compressedData)
    return btoa(compressedJson)
  } catch (error) {
    console.error('utm-helper: createUtmCookieValueFromUrl() error', error, url)
    return null
  }
}
